import {
  type FileData,
  getFiles,
  addMultiRecordingFile,
  getMultiRecordingFiles,
  type MultiRecordingFileData,
} from "@/services/files";
import { create } from "zustand";
import { t } from "i18next";
import { uploadAudioToS3, type UploadResult } from "@/lib/s3/uploadAudioS3";
import { getNotesTemplates } from "@/services/notesTemplates";
import type { NoteTemplate } from "@/common/types/notesTemplates";
import { usePostOnboardingStore } from "./postOnboarding.store";

export interface VisitState {
  file: FileData | null;
  notes: NoteTemplate[];
  loading: boolean;
  error: string | null;
  getFile: (
    body: { userId: number; fileId: number },
    showLoading?: boolean
  ) => Promise<void>;

  loadingMultiRecordingFile: boolean;
  errorMultiRecordingFile: string | null;
  uploadMultiRecordingFile: (parameters: {
    file: File | Blob;
    fileId: number;
    userId: number;
    duration: number;
  }) => Promise<void>;

  multiRecordingFiles: MultiRecordingFileData[];
  multiRecordingFilesLoading: boolean;
  multiRecordingFilesError: string | null;
  getMultiRecordingFiles: (
    parameters: { fileId: number },
    showLoading?: boolean
  ) => Promise<void>;
  reset: () => void;
}

export const useVisitStore = create<VisitState>()((set) => ({
  file: null,
  notes: [],
  loading: false,
  errorMultiRecordingFile: null,

  loadingMultiRecordingFile: false,
  error: null,

  multiRecordingFiles: [],
  multiRecordingFilesLoading: false,
  multiRecordingFilesError: null,

  getFile: async (
    body: { userId: number; fileId: number },
    showLoading: boolean = true
  ): Promise<void> => {
    set({ loading: showLoading, error: null });
    try {
      const { files, error } = await getFiles({
        userId: body.userId,
        fileId: body.fileId,
      });
      const notes = await getNotesTemplates(body);
      if (!error) {
        const file = files?.[0] || null;
        set({ file: file, loading: false, notes: notes });
        return;
      } else {
        set({
          error: t("error.unknown"),
          file: null,
          loading: false,
          notes: [],
        });
        return;
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        set({ file: null, error: error.message, loading: false, notes: [] });
      } else {
        set({
          file: null,
          error: t("error.unknown"),
          loading: false,
          notes: [],
        });
      }
    }
  },

  getMultiRecordingFiles: async (
    { fileId },
    showLoading: boolean = true
  ): Promise<void> => {
    set({ loading: showLoading, error: null });
    try {
      const { files, error } = await getMultiRecordingFiles({ fileId });
      if (!error) {
        set({ multiRecordingFiles: files, loading: false });
      } else {
        set({
          error: t("error.unknown"),
          multiRecordingFiles: [],
          loading: false,
        });
      }
    } catch (error: unknown) {
      set({
        multiRecordingFiles: [],
        error: error instanceof Error ? error.message : t("error.unknown"),
        loading: false,
      });
    }
  },

  uploadMultiRecordingFile: async (parameters): Promise<void> => {
    set({ loadingMultiRecordingFile: true, errorMultiRecordingFile: null });
    try {
      // 1: upload file to S3
      const uploadResult: UploadResult = await uploadAudioToS3(
        parameters.file,
        parameters.userId
      );

      if (!uploadResult.ok || !uploadResult.key) {
        set({
          loadingMultiRecordingFile: false,
          errorMultiRecordingFile:
            uploadResult.error || "Error al subir el archivo",
        });
        return;
      }

      // 2: add file to database
      const addRecordingPayload = {
        userId: parameters.userId,
        fileId: parameters.fileId,
        name: uploadResult.key,
        duration: parameters.duration,
      };

      const addRecordingResponse =
        await addMultiRecordingFile(addRecordingPayload);

      if (addRecordingResponse.error) {
        set({
          loadingMultiRecordingFile: false,
          errorMultiRecordingFile:
            addRecordingResponse.message || "Error al agregar la grabación",
        });
        return;
      }

      set({ loadingMultiRecordingFile: false, errorMultiRecordingFile: null });
      usePostOnboardingStore.getState().markDone();
    } catch (error: unknown) {
      set({
        loadingMultiRecordingFile: false,
        errorMultiRecordingFile:
          error instanceof Error ? error.message : t("error.unknown"),
      });
    }
  },
  reset: (): void => {
    set({
      file: null,
      notes: [],
      loading: false,
      errorMultiRecordingFile: null,
      loadingMultiRecordingFile: false,
      error: null,
      multiRecordingFiles: [],
      multiRecordingFilesLoading: false,
      multiRecordingFilesError: null,
    });
  },
}));
